import { Box } from '@mui/system';
import { Button } from '@mui/material';
import { usePostApiRequestsMutation } from 'store/userOrg/enhancedUserOrgApi';
import { FormProvider, useForm } from 'react-hook-form';
import { FormInputText } from 'components/forms/reactHookForm/FormInputText';
import useGAHandleClick from 'hooks/useGAHandleClick';
import { throwSuccessToast } from 'utils';
import { successMessages } from 'constants/messages';
import BaseModal from 'components/ui/Modal/BaseModal';
import useModal from 'hooks/useModal';
import { LoadingButton } from '@mui/lab';
import { useEffect } from 'react';
import { IconSend } from 'utils/icons';

interface AccessRequestFormProps {
    disabled: boolean;
}

const DEFAULT_VALUES = {
    shortName: '',
};

type AccessRequestFormValues = typeof DEFAULT_VALUES;

export default function AccessRequestForm({ disabled }: AccessRequestFormProps) {
    const googleAnalytics = useGAHandleClick();
    const { open, handleOpen, handleClose } = useModal();

    const [createRequest, { isLoading }] = usePostApiRequestsMutation();

    const methods = useForm<AccessRequestFormValues>({
        defaultValues: DEFAULT_VALUES,
    });

    const {
        handleSubmit,
        reset,
        formState: { isDirty, isValid },
    } = methods;

    useEffect(() => {
        if (!open) {
            reset(DEFAULT_VALUES);
        }
    }, [open]);

    const onSubmit = async (data: AccessRequestFormValues) => {
        await createRequest({
            requestCreate: {
                ProjectShortName: data.shortName,
                RequestType: 'SelfOnboarding',
                ContactEmail: null,
                ProjectResourceName: null,
                ProviderId: null,
            },
        }).unwrap();

        throwSuccessToast({ message: successMessages.requested });

        googleAnalytics({
            label: 'RequestProjectAccess',
            action: 'Request',
            category: 'Requesting',
        });

        handleClose();
    };

    return (
        <>
            <Button
                disabled={disabled}
                variant='outlined'
                onClick={() => {
                    handleOpen();
                    googleAnalytics({
                        label: 'OpenRequestProjectAccessModal',
                        action: 'Open',
                        category: 'Modal',
                    });
                }}
                startIcon={<IconSend size={20} />}>
                Request project access
            </Button>
            <BaseModal
                fullWidth
                maxWidth='xs'
                open={open}
                onClose={handleClose}
                title='Request project access'
                actions={
                    <>
                        <Button variant='outlined' onClick={handleClose}>
                            Cancel
                        </Button>
                        <LoadingButton
                            color='primary'
                            variant='contained'
                            loading={isLoading}
                            disabled={!isDirty || !isValid}
                            onClick={handleSubmit(onSubmit)}>
                            Request
                        </LoadingButton>
                    </>
                }>
                <Box>
                    <FormProvider {...methods}>
                        <FormInputText
                            name='shortName'
                            required
                            label='Short Name'
                            margin='normal'
                            fullWidth
                            helperText='Short Name is required'
                        />
                    </FormProvider>
                </Box>
            </BaseModal>
        </>
    );
}
